// Rich clipboard writing: one write carrying both an HTML and a plain-text
// flavor. Rich paste targets (Apple Mail, Outlook, Word) take the HTML and
// show real lists/bold; plain targets (terminals, ticket text fields) take
// the text. The HTML passed in must come from markdownToEmailHtml, which
// escapes all input — it is never raw user/model HTML.

interface RichClipboardPayload {
  html: string;
  text: string;
}

// Legacy path for browsers/iframes where the async Clipboard API is blocked:
// a hidden contenteditable region carries the HTML flavor through
// execCommand("copy") in most engines.
function legacyExecCopy({ html, text }: RichClipboardPayload): boolean {
  const container = document.createElement("div");
  container.innerHTML = html;
  container.style.position = "fixed";
  container.style.opacity = "0";
  // Preferred: hand the exact HTML to the clipboard from the copy event, so no
  // computed styles from the host page travel with it and the paste target
  // applies its own fonts and paragraph spacing.
  const onCopy = (e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    e.clipboardData.setData("text/html", html);
    e.clipboardData.setData("text/plain", text);
    e.preventDefault();
  };
  document.addEventListener("copy", onCopy);
  // Fallback for hosts without ClipboardEvent.clipboardData (IE-based
  // embedded browsers): a selection copy serializes computed styles, and the
  // page's CSS reset (Tailwind preflight: list-style none) would cost Word-based
  // Outlook its bullets. Pin only the list styling; leave paragraphs and fonts
  // to the paste target.
  container.querySelectorAll<HTMLElement>("ul, ol").forEach((el) => {
    el.style.listStyleType = el.tagName === "UL" ? "disc" : "decimal";
    el.style.paddingLeft = "1.5em";
  });
  container.setAttribute("contenteditable", "true");
  document.body.appendChild(container);
  const selection = window.getSelection();
  const range = document.createRange();
  range.selectNodeContents(container);
  selection?.removeAllRanges();
  selection?.addRange(range);
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  } finally {
    document.removeEventListener("copy", onCopy);
  }
  selection?.removeAllRanges();
  document.body.removeChild(container);
  if (!ok) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    ok = document.execCommand("copy");
    document.body.removeChild(textarea);
  }
  return ok;
}

/**
 * Write both flavors to the clipboard. Resolves true when any strategy
 * succeeded. Order: ClipboardItem (both flavors) → execCommand (still both
 * flavors — it works where the async API is blocked by permissions policy,
 * e.g. extension iframes, as long as a user gesture is in flight) →
 * writeText (plain-only last resort).
 */
export async function writeClipboardRich(payload: RichClipboardPayload): Promise<boolean> {
  if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([payload.html], { type: "text/html" }),
          "text/plain": new Blob([payload.text], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch {
      // Blocked (permissions policy, focus loss) — try the legacy rich path.
    }
  }
  if (legacyExecCopy(payload)) return true;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(payload.text);
      return true;
    } catch {
      // fall through
    }
  }
  return false;
}
